import React, { useEffect, useState } from "react";
import "../../styles/HealthDashboard.css";
import GloablButton from "../GloablButton";
import SaveSettingsIcon from "../../icons/SaveSettingsIcon";
import vendorDashboardController from "../../controllers/vendorDashboardController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const toDisplayLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCount = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const numberValue = Number(value);
  return Number.isNaN(numberValue)
    ? String(value)
    : numberValue.toLocaleString();
};

const formatPercentage = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (String(value).includes("%")) return String(value);

  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);

  const percentage = numberValue > 0 && numberValue <= 1
    ? numberValue * 100
    : numberValue;

  return `${percentage.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;
};

const formatMinutes = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (/[a-z]/i.test(String(value))) return String(value);
  return `${value}m`;
};

const normalizeCategories = (categories) => {
  const categoryList = Array.isArray(categories)
    ? categories
    : Object.entries(categories || {}).map(([name, values]) => ({
        name,
        ...(typeof values === "object" ? values : { total: values }),
      }));

  return categoryList.map((category) => {
    const total = firstDefined(
      category.total,
      category.totalVendors,
      category.vendorCount,
      0,
    );
    const online = firstDefined(
      category.online,
      category.onlineVendors,
      category.onlineCount,
      0,
    );
    const offline = firstDefined(
      category.offline,
      category.offlineVendors,
      category.offlineCount,
      Number(total) - Number(online),
      0,
    );

    return {
      name: toDisplayLabel(
        firstDefined(
          category.name,
          category.category,
          category.categoryType,
        ),
      ),
      total: Number(total) || 0,
      online: Number(online) || 0,
      offline: Math.max(Number(offline) || 0, 0),
    };
  });
};

function HealthDashboard() {
  useTranslationSnapshot();
  const [minRating, setMinRating] = useState("3.5");
  const [maxEta, setMaxEta] = useState("60");
  const [statCards, setStatCards] = useState([]);
  const [categoryCards, setCategoryCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchDashboard = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await vendorDashboardController.getHealthDashboard({
          jwt: getStoredToken(),
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const responsePayload = response?.data;
        const payload =
          responsePayload?.data ??
          responsePayload?.result ??
          responsePayload ??
          {};
        const overview = payload.overview ?? payload.summary ?? payload;
        const threshold =
          payload.eligibilityThreshold ?? payload.thresholds ?? payload;

        if (!isCurrentRequest) return;

        setStatCards([
          {
            label: "Total Active Vendors",
            value: formatCount(
              firstDefined(
                overview.totalActiveVendors,
                overview.activeVendors,
                overview.activeVendorCount,
              ),
            ),
            sub: "Across all regions",
            icon: null,
          },
          {
            label: "Butler QR Scan Rate",
            value: formatPercentage(
              firstDefined(
                overview.butlerQrScanRate,
                overview.butlerQRScanRate,
                overview.qrScanRate,
              ),
            ),
            sub: "Fulfillment compliance",
            icon: null,
          },
          {
            label: "Full Fulfillment",
            value: formatPercentage(
              firstDefined(
                overview.fullFulfillment,
                overview.fullFulfillmentRate,
                overview.fulfillmentRate,
              ),
            ),
            sub: "Average order success",
            icon: null,
          },
          {
            label: "Time Spent in App",
            value: formatMinutes(
              firstDefined(
                overview.timeSpentInApp,
                overview.averageTimeSpentInApp,
                overview.avgTimeSpentInApp,
              ),
            ),
            sub: "",
            icon: null,
          },
        ]);

        setCategoryCards(
          normalizeCategories(
            firstDefined(
              payload.vendorsByCategory,
              payload.vendorCategories,
              payload.categoryBreakdown,
              payload.categories,
              [],
            ),
          ),
        );

        const minimumRating = firstDefined(
          threshold.minimumRating,
          threshold.minRating,
        );
        const maximumEta = firstDefined(
          threshold.maxEtaMinutes,
          threshold.maximumEta,
          threshold.maxEta,
        );

        if (minimumRating !== undefined) {
          setMinRating(String(minimumRating));
        }
        if (maximumEta !== undefined) {
          setMaxEta(String(maximumEta));
        }
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setStatCards([]);
        setCategoryCards([]);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load dashboard.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  return (
    <div className="health-dashboard-wrapper">
      <div className="health-dashboard-header">
        <div className="health-dashboard-title">Discovery Health Dashboard</div>
        <div className="health-dashboard-subtitle">High-level overview of discovery surfaces and platform availability.</div>
      </div>

      {loading && (
        <div className="health-dashboard-state">{translateLabel("txt_business_loading_dashboard", "Loading dashboard...")}</div>
      )}
      {!loading && error && (
        <div className="health-dashboard-state health-dashboard-state-error">
          {translateText(error)}
        </div>
      )}

      <div className="health-stat-row">
        {statCards.map((card) => (
          <div className="health-stat-card" key={card.label}>
            <div className="health-stat-card-top">
              <span className="health-stat-card-label">{translateText(card.label)}</span>
              <span className="health-stat-card-icon">{card.icon}</span>
            </div>
            <div className="health-stat-card-value">{card.value}</div>
            {card.sub && <div className="health-stat-card-sub">{card.sub}</div>}
          </div>
        ))}
      </div>

      <div className="health-section">
        <div className="health-section-title">Vendors by Category</div>
        <div className="health-section-subtitle">Online vs Offline vendor availability per category</div>
        <div className="health-category-row">
          {!loading && !error && categoryCards.length === 0 && (
            <div className="health-dashboard-state">No category data found.</div>
          )}
          {categoryCards.map((cat) => {
            const onlinePct = cat.total
              ? Math.round((cat.online / cat.total) * 100)
              : 0;
            return (
              <div className="health-category-card" key={cat.name}>
                <div className="health-category-name">{cat.name}</div>
                <div className="health-category-total">
                  <span className="health-category-total-num">{cat.total}</span>
                  <span className="health-category-total-label"> {translateLabel("txt_end_user_total", "total")}</span>
                </div>
                <div className="health-category-stat-row">
                  <span className="health-dot health-dot-online" />
                  <span className="health-category-stat-label">{translateLabel("txt_admin_online", "Online")}</span>
                  <span className="health-category-stat-value">{cat.online}</span>
                </div>
                <div className="health-category-stat-row">
                  <span className="health-dot health-dot-offline" />
                  <span className="health-category-stat-label">{translateLabel("txt_offline", "Offline")}</span>
                  <span className="health-category-stat-value">{cat.offline}</span>
                </div>
                <div className="health-category-bar-bg">
                  <div className="health-category-bar-fill" style={{ width: `${onlinePct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="health-section">
        <div className="health-threshold-card">
          <div className="health-section-title">Eligibility Threshold</div>
          <div className="health-section-subtitle">Absolute minimums required to appear in Top Picks or Featured</div>
          <div className="health-threshold-fields">
            <div className="health-threshold-field-group">
              <label className="health-threshold-field-label">Minimum Rating</label>
              <input
                className="health-threshold-field-input"
                type="number"
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
              />
            </div>
            <div className="health-threshold-field-group">
              <label className="health-threshold-field-label">
                Max ETA(Minutes)
                <span className="health-threshold-info-icon" title={translateLabel("txt_admin_maximum_estimated_time_of_arrival_in_minutes", "Maximum estimated time of arrival in minutes")}>ℹ</span>
              </label>
              <input
                className="health-threshold-field-input"
                type="number"
                value={maxEta}
                onChange={(e) => setMaxEta(e.target.value)}
              />
            </div>
          </div>
          <div className="health-threshold-footer">
            <div className="health-threshold-warning">
              <span className="health-threshold-warning-text">Changes impact live discover rankings immediately.</span>
            </div>
            <GloablButton
              text={translateLabel("txt_admin_save_settings", "Save Settings")}
              width="161px"
              height="44px"
              backgroundColor="#FA9C03"
              icon={<SaveSettingsIcon />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default HealthDashboard;
